import { useRef, useState, type RefObject } from 'react';
import { IonAlert, IonButton, IonContent, IonHeader, IonIcon, IonNav, IonNote, IonPage, IonTitle, IonToolbar } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { alarmOutline, lockClosedOutline, optionsOutline, trashOutline, closeOutline, checkmarkOutline } from 'ionicons/icons';
import { TransitPolicyRule } from '../models/TransitPolicy';
import { EventClassification, EventTriggerSource } from '../models/DeviceEvent';
import { FlapState, MotionSensorState } from '../models/FrameMetadata';
import { EditDoorPolicySoundPickerView, getSoundLabel } from './edit-door-policy/EditDoorPolicySoundPicker';
import { EditDoorPolicyLockPickerView, type LockActionOption, type LockDurationOption } from './edit-door-policy/EditDoorPolicyLockPicker';
import { EditDoorPolicyTriggerSourcePickerView } from './edit-door-policy/EditDoorPolicyTriggerSourcePicker';
import { EditDoorPolicyClassificationPickerView } from './edit-door-policy/EditDoorPolicyClassificationPicker';
import { EditDoorPolicyMotionSensorStatePickerView } from './edit-door-policy/EditDoorPolicyMotionSensorStatePicker';
import { EditDoorPolicyFlapStatePickerView } from './edit-door-policy/EditDoorPolicyFlapStatePicker';
import { EditDoorPolicyTimeRangePickerView } from './edit-door-policy/EditDoorPolicyTimeRangePicker';
import { EditDoorPolicyRfidPickerView } from './edit-door-policy/EditDoorPolicyRfidPicker';
import RfidChip from '../components/RfidChip';
import styles from './DoorPolicyModal.module.css';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import { formatDurationLabel, getClassificationLabel, getFlapStateLabel, getMotionSensorStateLabel, getTriggerSourceLabel } from '../utils/doorPolicyTranslations';
import { useDeveloperModeQuery } from '../query/developerModeQuery';

const DEFAULT_LOCKOUT_DURATION_MS = 30_000;

type RuleProp = TransitPolicyRule | undefined;
type RuleResolver = RuleProp | (() => RuleProp);

interface EditDoorPolicyRuleProps {
    dismiss: (data?: string | null | undefined | number, role?: string) => void;
    deviceId: string;
    onDelete: (ruleIndex?: number) => void;
    onSave: (rule: TransitPolicyRule, ruleIndex?: number) => void;
    rule?: RuleResolver;
    ruleIndex?: number;
}

const resolveRule = (rule?: RuleResolver) => {
    if (typeof rule === 'function') {
        return rule();
    }
    return rule;
};

const formatList = (values: Array<string | number>, orLabel = 'or') => {
    if (values.length === 1) {
        return `${values[0]}`;
    }
    return `${values.slice(0, -1).join(', ')} ${orLabel} ${values[values.length - 1]}`;
};

const toArray = <T,>(value?: T | T[]) => {
    if (value === undefined) {
        return [];
    }
    return Array.isArray(value) ? value : [value];
};

const resolveLockActionOption = (action?: TransitPolicyRule['action']): LockActionOption => {
    if (!action) {
        return 'none';
    }
    if (action.lock === undefined) {
        if (action.lockoutDuration !== undefined || action.final) {
            return 'lock';
        }
        return 'none';
    }
    return action.lock ? 'lock' : 'unlock';
};

const resolveLockDurationOption = (action?: TransitPolicyRule['action']): LockDurationOption => {
    if (!action) {
        return 'default';
    }
    if (action.lockoutDuration !== undefined) {
        return 'for_duration';
    }
    if (action.final) {
        return 'until_event_end';
    }
    return 'default';
};

interface EditDoorPolicyRuleMainProps extends EditDoorPolicyRuleProps {
    navRef: RefObject<HTMLIonNavElement>;
}

const EditDoorPolicyRuleMain = ({ dismiss, rule, ruleIndex, navRef, deviceId, onSave, onDelete }: EditDoorPolicyRuleMainProps) => {
    const resolvedRule = resolveRule(rule);
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
    const [triggerSources, setTriggerSources] = useState<EventTriggerSource[]>(
        () => toArray(resolvedRule?.criteria?.eventTriggerSource)
    );
    const [classifications, setClassifications] = useState<EventClassification[]>(
        () => toArray(resolvedRule?.criteria?.eventClassification)
    );
    const [motionSensorStates, setMotionSensorStates] = useState<MotionSensorState[]>(
        () => toArray(resolvedRule?.criteria?.motionSensorState)
    );
    const [flapStates, setFlapStates] = useState<FlapState[]>(
        () => toArray(resolvedRule?.criteria?.flapState)
    );
    const [timeRangesInput, setTimeRangesInput] = useState(
        () => {
            const values = toArray(resolvedRule?.criteria?.timeRange);
            return values.length ? values.join(', ') : '';
        }
    );
    const [rfidCodesInput, setRfidCodesInput] = useState(
        () => {
            const values = toArray(resolvedRule?.criteria?.rfidCode);
            return values.length ? values.join(', ') : '';
        }
    );
    const [lockActionOption, setLockActionOption] = useState<LockActionOption>(() => resolveLockActionOption(resolvedRule?.action));
    const [lockDurationOption, setLockDurationOption] = useState<LockDurationOption>(() => resolveLockDurationOption(resolvedRule?.action));
    const [lockoutDurationMs, setLockoutDurationMs] = useState<number | undefined>(() => resolvedRule?.action?.lockoutDuration);
    const [soundInput, setSoundInput] = useState(() => resolvedRule?.action?.sound ?? '');
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const developerModeQuery = useDeveloperModeQuery();
    const developerMode = !!developerModeQuery.data;

    const toggleSelection = <T,>(current: T[], value: T, setter: (next: T[]) => void) => {
        if (current.includes(value)) {
            setter(current.filter((item) => item !== value));
        } else {
            setter([...current, value]);
        }
    };

    const triggerSourceText = triggerSources.length
        ? formatList(triggerSources.map((source) => getTriggerSourceLabel(tModals, source)), tModals('door_policy.rule_summary.or'))
        : tModals('door_policy.edit_rule.any_trigger_source');
    const classificationText = classifications.length
        ? formatList(classifications.map((classification) => getClassificationLabel(tModals, classification)), tModals('door_policy.rule_summary.or'))
        : tModals('door_policy.edit_rule.any_classification');
    const motionSensorStateText = motionSensorStates.length
        ? formatList(motionSensorStates.map((state) => getMotionSensorStateLabel(tModals, state)), tModals('door_policy.rule_summary.or'))
        : tModals('door_policy.edit_rule.any_motion_sensor_state');
    const flapStateText = flapStates.length
        ? formatList(flapStates.map((state) => getFlapStateLabel(tModals, state)), tModals('door_policy.rule_summary.or'))
        : tModals('door_policy.edit_rule.any_flap_state');
    const timeRangeText = timeRangesInput.trim().length ? timeRangesInput : tModals('door_policy.edit_rule.any_time');
    const rfidCodesList = rfidCodesInput
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    const rfidCodesText = rfidCodesList.length ? '' : tModals('door_policy.edit_rule.any_microchip');
    const lockDurationLabel = formatDurationLabel(
        tModals,
        lockoutDurationMs && lockoutDurationMs > 0 ? lockoutDurationMs : DEFAULT_LOCKOUT_DURATION_MS,
        { short: true }
    );
    const lockActionText = (() => {
        switch (lockActionOption) {
            case 'none':
                return tModals('door_policy.edit_rule.no_lock_action');
            case 'unlock':
                return tModals('door_policy.edit_rule.unlock_the_door');
            case 'lock':
                if (lockDurationOption === 'until_event_end') {
                    return tModals('door_policy.edit_rule.lock_until_event_end');
                }
                if (lockDurationOption === 'for_duration') {
                    return tModals('door_policy.edit_rule.lock_for_duration', { duration: lockDurationLabel });
                }
                return tModals('door_policy.edit_rule.lock_the_door');
            default:
                return tModals('door_policy.edit_rule.no_lock_action');
        }
    })();
    const trimmedSoundInput = soundInput.trim();
    const soundLabel = getSoundLabel(tModals, trimmedSoundInput);
    const soundActionText = trimmedSoundInput.length
        ? tModals('door_policy.edit_rule.play_sound', { sound: soundLabel })
        : tModals('door_policy.edit_rule.no_sound');
    const isTriggerSourceSet = triggerSources.length > 0;
    const isClassificationSet = classifications.length > 0;
    const isMotionSensorStateSet = motionSensorStates.length > 0;
    const isFlapStateSet = flapStates.length > 0;
    const isTimeRangeSet = timeRangesInput.trim().length > 0;
    const isRfidSet = rfidCodesList.length > 0;
    const isLockActionSet = lockActionOption !== 'none';
    const isSoundSet = soundInput.trim().length > 0;
    const shouldShowMotionSensorState = developerMode || isMotionSensorStateSet;
    const shouldShowFlapState = developerMode || isFlapStateSet;

    const openSoundPicker = () => {
        navRef.current?.push(EditDoorPolicySoundPickerView, {
            soundInput,
            onSelectSound: setSoundInput,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openTriggerSourcePicker = () => {
        navRef.current?.push(EditDoorPolicyTriggerSourcePickerView, {
            triggerSources,
            onSelectTriggerSources: setTriggerSources,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openClassificationPicker = () => {
        navRef.current?.push(EditDoorPolicyClassificationPickerView, {
            classifications,
            onSelectClassifications: setClassifications,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openMotionSensorStatePicker = () => {
        navRef.current?.push(EditDoorPolicyMotionSensorStatePickerView, {
            motionSensorStates,
            onSelectMotionSensorStates: setMotionSensorStates,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openFlapStatePicker = () => {
        navRef.current?.push(EditDoorPolicyFlapStatePickerView, {
            flapStates,
            onSelectFlapStates: setFlapStates,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openTimeRangePicker = () => {
        navRef.current?.push(EditDoorPolicyTimeRangePickerView, {
            timeRange: timeRangesInput,
            onSelectTimeRange: setTimeRangesInput,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openRfidPicker = () => {
        navRef.current?.push(EditDoorPolicyRfidPickerView, {
            deviceId,
            rfidCodes: rfidCodesList,
            onSelectRfidCodes: (codes: string[]) => setRfidCodesInput(codes.join(', ')),
            onClose: () => navRef.current?.pop(),
        });
    };

    const saveRule = () => {
        if (!resolvedRule) {
            dismiss(undefined, 'cancel');
            return;
        }

        const criteria: TransitPolicyRule['criteria'] = {};
        if (triggerSources.length) {
            criteria.eventTriggerSource = triggerSources;
        }
        if (classifications.length) {
            criteria.eventClassification = classifications;
        }
        if (motionSensorStates.length) {
            criteria.motionSensorState = motionSensorStates;
        }
        if (flapStates.length) {
            criteria.flapState = flapStates;
        }
        if (timeRangesInput.trim()) {
            criteria.timeRange = timeRangesInput.trim();
        }
        if (rfidCodesList.length) {
            criteria.rfidCode = rfidCodesList;
        }

        const action: TransitPolicyRule['action'] = {};
        switch (lockActionOption) {
            case 'unlock':
                action.lock = false;
                break;
            case 'lock':
                action.lock = true;
                if (lockDurationOption === 'until_event_end') {
                    action.final = true;
                } else if (lockDurationOption === 'for_duration') {
                    const durationMs = lockoutDurationMs && lockoutDurationMs > 0 ? lockoutDurationMs : DEFAULT_LOCKOUT_DURATION_MS;
                    action.lockoutDuration = durationMs;
                }
                break;
            case 'none':
            default:
                break;
        }
        if (soundInput.trim()) {
            action.sound = soundInput.trim();
        }

        const updatedRule = new TransitPolicyRule({
            ...resolvedRule,
            criteria: Object.keys(criteria).length ? criteria : undefined,
            action: Object.keys(action).length ? action : undefined,
        });

        onSave(updatedRule, ruleIndex);
        dismiss(undefined, 'confirm');
    };
    const openLockPicker = () => {
        navRef.current?.push(EditDoorPolicyLockPickerView, {
            lockActionOption,
            lockDurationOption,
            lockDurationMs: lockoutDurationMs,
            onSelectLock: (nextOption: LockActionOption, nextDurationOption: LockDurationOption, nextDurationMs?: number) => {
                setLockActionOption(nextOption);
                setLockDurationOption(nextDurationOption);
                if (nextOption === 'lock' && nextDurationOption === 'for_duration') {
                    setLockoutDurationMs(nextDurationMs ?? DEFAULT_LOCKOUT_DURATION_MS);
                } else {
                    setLockoutDurationMs(undefined);
                }
            },
            onClose: () => navRef.current?.pop(),
        });
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton
                            color="medium"
                            onClick={() => dismiss(undefined, 'cancel')}
                            aria-label={tCommon('buttons.cancel')}
                        >
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>
                        {ruleIndex !== undefined
                            ? tModals('door_policy.edit_rule.title_with_index', { index: ruleIndex + 1 })
                            : tModals('door_policy.edit_rule.title')}
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton
                            color="primary"
                            onClick={saveRule}
                            aria-label={tCommon('buttons.save')}
                        >
                            <IonIcon slot="icon-only" icon={checkmarkOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                {!resolvedRule && (
                    <IonNote className="ion-padding">
                        {tModals('door_policy.edit_rule.not_found')}
                    </IonNote>
                )}
                {resolvedRule && (
                    <div className={styles.wrapper}>
                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>{tModals('door_policy.sections.when_edit')}</h2>
                            <div
                                className={`${styles.ruleCardBase} ${styles.ruleCardRow} ${styles.ruleCardButton} ${!isTriggerSourceSet ? styles.ruleCardInactive : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openTriggerSourcePicker}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openTriggerSourcePicker();
                                    }
                                }}
                            >
                                <div className={`${styles.cardIcon} ${styles.iconLogic}`}>
                                           <IonIcon icon={optionsOutline} />
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardLabel}>{tModals('door_policy.edit_rule.trigger_source')}</div>
                                    <div className={styles.cardText}>{triggerSourceText}</div>
                                </div>
                            </div>
                            <div
                                className={`${styles.ruleCardBase} ${styles.ruleCardRow} ${styles.ruleCardButton} ${!isClassificationSet ? styles.ruleCardInactive : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openClassificationPicker}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openClassificationPicker();
                                    }
                                }}
                            >
                                <div className={`${styles.cardIcon} ${styles.iconLogic}`}>
                                    <IonIcon icon={optionsOutline} />
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardLabel}>{tModals('door_policy.edit_rule.classification')}</div>
                                    <div className={styles.cardText}>{classificationText}</div>
                                </div>
                            </div>
                            <div
                                className={`${styles.ruleCardBase} ${styles.ruleCardRow} ${styles.ruleCardButton} ${!isTimeRangeSet ? styles.ruleCardInactive : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openTimeRangePicker}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openTimeRangePicker();
                                    }
                                }}
                            >
                                <div className={`${styles.cardIcon} ${styles.iconLogic}`}>
                                    <IonIcon icon={optionsOutline} />
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardLabel}>{tModals('door_policy.edit_rule.time_range')}</div>
                                    <div className={styles.cardText}>{timeRangeText}</div>
                                </div>
                            </div>
                            <div
                                className={`${styles.ruleCardBase} ${styles.ruleCardRow} ${styles.ruleCardButton} ${!isRfidSet ? styles.ruleCardInactive : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openRfidPicker}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openRfidPicker();
                                    }
                                }}
                            >
                                <div className={`${styles.cardIcon} ${styles.iconLogic}`}>
                                    <IonIcon icon={optionsOutline} />
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardLabel}>{tModals('door_policy.edit_rule.microchips')}</div>
                                    <div className={styles.cardText}>{rfidCodesText}</div>
                                    {rfidCodesList.length > 0 && (
                                        <div className={styles.rfidChipList}>
                                            {rfidCodesList.map((rfidCode) => (
                                                <RfidChip key={rfidCode} rfidCode={rfidCode} deviceId={deviceId} />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            {shouldShowMotionSensorState && (
                                <div
                                    className={`${styles.ruleCardBase} ${styles.ruleCardRow} ${styles.ruleCardButton} ${!isMotionSensorStateSet ? styles.ruleCardInactive : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openMotionSensorStatePicker}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            openMotionSensorStatePicker();
                                        }
                                    }}
                                >
                                    <div className={`${styles.cardIcon} ${styles.iconLogic}`}>
                                        <IonIcon icon={optionsOutline} />
                                    </div>
                                    <div className={styles.cardBody}>
                                        <div className={styles.cardLabel}>{tModals('door_policy.edit_rule.motion_sensor_state')}</div>
                                        <div className={styles.cardText}>{motionSensorStateText}</div>
                                    </div>
                                </div>
                            )}
                            {shouldShowFlapState && (
                                <div
                                    className={`${styles.ruleCardBase} ${styles.ruleCardRow} ${styles.ruleCardButton} ${!isFlapStateSet ? styles.ruleCardInactive : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openFlapStatePicker}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            openFlapStatePicker();
                                        }
                                    }}
                                >
                                    <div className={`${styles.cardIcon} ${styles.iconLogic}`}>
                                        <IonIcon icon={optionsOutline} />
                                    </div>
                                    <div className={styles.cardBody}>
                                        <div className={styles.cardLabel}>{tModals('door_policy.edit_rule.flap_state')}</div>
                                        <div className={styles.cardText}>{flapStateText}</div>
                                    </div>
                                </div>
                            )}
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>{tModals('door_policy.sections.then_edit')}</h2>
                            <div
                                className={`${styles.ruleCardBase} ${styles.ruleCardRow} ${styles.ruleCardButton} ${!isLockActionSet ? styles.ruleCardInactive : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openLockPicker}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openLockPicker();
                                    }
                                }}
                            >
                                <div className={`${styles.cardIcon} ${styles.iconAction}`}>
                                    <IonIcon icon={lockClosedOutline} />
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardLabel}>{tModals('door_policy.edit_rule.door_lock')}</div>
                                    <div className={styles.cardText}>{lockActionText}</div>
                                </div>
                            </div>
                            <div
                                className={`${styles.ruleCardBase} ${styles.ruleCardRow} ${styles.ruleCardButton} ${!isSoundSet ? styles.ruleCardInactive : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openSoundPicker}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        openSoundPicker();
                                    }
                                }}
                            >
                                <div className={`${styles.cardIcon} ${styles.iconAlarm}`}>
                                    <IonIcon icon={alarmOutline} />
                                </div>
                                <div className={styles.cardBody}>
                                    <div className={styles.cardLabel}>{tModals('door_policy.edit_rule.sound')}</div>
                                    <div className={styles.cardText}>{soundActionText}</div>
                                </div>
                            </div>
                        </section>
                        {ruleIndex !== undefined && (
                            <section className={styles.section}>
                                <IonButton
                                    expand="block"
                                    color="danger"
                                    fill="solid"
                                    onClick={() => setIsDeleteAlertOpen(true)}
                                >
                                    <IonIcon slot="start" icon={trashOutline} />
                                    {tModals('door_policy.edit_rule.delete_button')}
                                </IonButton>
                            </section>
                        )}
                    </div>
                )}
                <IonAlert
                    isOpen={isDeleteAlertOpen}
                    onDidDismiss={() => setIsDeleteAlertOpen(false)}
                    header={tModals('door_policy.edit_rule.delete_confirm_title')}
                    buttons={[
                        {
                            text: tCommon('buttons.cancel'),
                            role: 'cancel',
                        },
                        {
                            text: tCommon('buttons.delete'),
                            role: 'destructive',
                            handler: () => {
                                onDelete(ruleIndex);
                                dismiss(undefined, 'confirm');
                            },
                        },
                    ]}
                />
            </IonContent>
        </IonPage>
    );
};

const EditDoorPolicyRuleMainView = (props: EditDoorPolicyRuleMainProps) => (
    <EditDoorPolicyRuleMain {...props} />
);

const EditDoorPolicyRule = ({ dismiss, rule, ruleIndex, deviceId, onSave, onDelete }: EditDoorPolicyRuleProps) => {
    const navRef = useRef<HTMLIonNavElement>(null);

    return (
        <IonNav
            ref={navRef}
            root={EditDoorPolicyRuleMainView}
            rootParams={{ dismiss, rule, ruleIndex, navRef, deviceId, onSave, onDelete }}
        />
    );
};

export default EditDoorPolicyRule;
